import { createMemo, createSignal, For, Show } from 'solid-js'
import { currentRef, openRef, useRefs } from '../store/view.ts'
import { cn } from '../utils/cn.ts'
import { Button } from './ui/Button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuGroupLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/Dropdown.tsx'

/*
  only HEAD is in the search index, so picking another ref changes what you're
  *browsing*, not what you're searching. the menu says so rather than quietly
  returning results from a branch you're not looking at
*/
export function RefPicker(props: { repo: string, open?: boolean, onOpenChange?: (open: boolean) => void }) {
  const refs = useRefs(() => props.repo)
  const [filter, setFilter] = createSignal('')

  const head = createMemo(() => refs.data?.find(it => it.isHead))
  const label = () => currentRef() ?? head()?.name ?? 'HEAD'

  const shown = createMemo(() => {
    const needle = filter().toLowerCase()
    return (refs.data ?? []).filter(it => !needle || it.name.toLowerCase().includes(needle))
  })

  return (
    <Show when={refs.data?.length}>
      <DropdownMenu open={props.open} onOpenChange={props.onOpenChange}>
        <DropdownMenuTrigger
          as={Button<'button'>}
          variant="outline"
          size="sm"
          class="h-6 shrink-0 gap-1 px-1.5 text-xs font-normal"
          title="switch branch or tag (r)"
        >
          <div class="i-lucide-git-branch size-3.5 shrink-0 text-muted-foreground" />
          <span class="max-w-40 truncate font-mono">{label()}</span>
          <div class="i-lucide-chevron-down size-3 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>

        <DropdownMenuContent class="max-h-96 w-64 overflow-y-auto">
          <div class="px-1 pb-1">
            <input
              value={filter()}
              onInput={e => setFilter(e.currentTarget.value)}
              placeholder="filter refs"
              class="w-full border border-border rounded bg-transparent px-1.5 py-1 text-xs outline-none placeholder:text-muted-foreground"
              spellcheck={false}
              // the menu treats keystrokes as typeahead and would swallow what you
              // type here (and close on space), so this input keeps its own keys
              onKeyDown={e => e.stopPropagation()}
            />
          </div>

          <DropdownMenuGroup>
            <DropdownMenuItem class="text-xs" onSelect={() => openRef(undefined)}>
              <div class={cn('i-lucide-check size-3.5 shrink-0', currentRef() && 'invisible')} />
              <span class="font-mono">{head()?.name ?? 'HEAD'}</span>
              <span class="ml-auto text-muted-foreground">indexed</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* the label has to live inside a group: kobalte reads its group context and
              throws without one, which took the whole menu down with it */}
          <DropdownMenuGroup>
            <DropdownMenuGroupLabel class="text-xs text-muted-foreground">
              browse only, not searchable
            </DropdownMenuGroupLabel>

            <For each={shown()}>
              {reference => (
                <DropdownMenuItem class="text-xs" onSelect={() => openRef(reference.name)}>
                  <div class={cn('i-lucide-check size-3.5 shrink-0', currentRef() !== reference.name && 'invisible')} />
                  <span class="truncate font-mono">{reference.name}</span>
                  <Show when={reference.kind === 'tag'}>
                    <span class="ml-auto text-muted-foreground">tag</span>
                  </Show>
                </DropdownMenuItem>
              )}
            </For>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </Show>
  )
}
